import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { OfficialFormSketch } from "../components/OfficialFormSketch";
import { ExtraQuestionsEditor } from "../components/QuestionDesign";
import { staff } from "../domain/data";
import { useForms, type FormCopy } from "../state/forms";
import { questionDesignError } from "../domain/questions";
import { ErrorFlash } from "../components/ErrorFlash";
import { PrototypeBanner, StaffNav } from "../components/ui";

export function StaffEditForm() {
  const { formId = "" } = useParams();
  const navigate = useNavigate();
  const { getForm, updateForm } = useForms();
  const form = getForm(formId);
  const [name, setName] = useState(form?.name ?? "");
  const [copy, setCopy] = useState<FormCopy | null>(form?.copy ?? null);
  const [error, setError] = useState("");

  if (!form || !copy) {
    return (
      <div className="app">
        <PrototypeBanner />
        <main className="shell">
          <p>Form not found.</p>
          <Link to="/staff/forms">Back to forms</Link>
        </main>
      </div>
    );
  }

  const yearForm = form;
  const formCopy = copy;
  const locked = yearForm.status !== "open";

  function patchCopy(next: Partial<FormCopy>) {
    setCopy((current) => (current ? { ...current, ...next } : current));
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (locked) return;
    const trimmed = name.trim();
    if (!trimmed || !formCopy.intro.trim()) {
      setError("Add a form title and description.");
      return;
    }
    if (formCopy.consents.some((item) => !item.label.trim())) {
      setError("Every permission question needs wording.");
      return;
    }
    const extraError = questionDesignError(formCopy.extraQuestions);
    if (extraError) {
      setError(extraError);
      return;
    }
    updateForm(yearForm.id, { name: trimmed, copy: formCopy });
    navigate(`/staff/forms/${yearForm.id}`);
  }

  return (
    <div className="app">
      <PrototypeBanner />
      <main className="shell">
        <StaffNav />
        <p className="meta">
          Staff · {staff.given} {staff.family} · design form
        </p>
        <h1>{locked ? "View form design" : "Design form"}</h1>
        <p className="lede">
          Edit the title and the description families see, then add extra
          questions for this year. The official permission form stays as
          written. Open a section if you want to see its fields.
        </p>

        {locked ? (
          <div className="flash">
            Saved years stay as families saw them. Open a new school year to
            change this year's form.
          </div>
        ) : null}

        <form className="design" onSubmit={onSubmit}>
          <section className="wire design-title">
            <div className="meta">Title and description</div>
            <div className="field">
              <label htmlFor="form-name">Form title</label>
              <input
                id="form-name"
                className="design-title-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={locked}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="form-intro">Description families see</label>
              <textarea
                id="form-intro"
                rows={3}
                value={copy.intro}
                onChange={(e) => patchCopy({ intro: e.target.value })}
                disabled={locked}
                required
              />
            </div>
          </section>

          <section className="design-section">
            <div className="wire design-section-head">
              <div>
                <div className="meta">This year only, unless you keep them</div>
                <strong>Extra questions</strong>
              </div>
              <div className="tag muted">
                {copy.extraQuestions.length} questions
              </div>
            </div>
            <ExtraQuestionsEditor
              questions={copy.extraQuestions}
              locked={locked}
              emptyLabel="Nothing extra yet"
              onChange={(extraQuestions) =>
                setCopy({ ...copy, extraQuestions })
              }
            />
          </section>

          <section className="design-section">
            <div className="wire design-section-head">
              <div>
                <div className="meta">Official form · wording stays fixed</div>
                <strong>2026-2027 permission form</strong>
              </div>
            </div>
            <OfficialFormSketch />
          </section>

          {error ? <ErrorFlash key={error} message={error} /> : null}

          <div className="actions">
            <button type="submit" disabled={locked}>
              Save design
            </button>
            <Link className="btn ghost" to={`/staff/forms/${form.id}`}>
              Cancel
            </Link>
            <Link className="btn ghost" to="/register">
              Preview register
            </Link>
          </div>
        </form>
      </main>
    </div>
  );
}
